import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";

const Card = ({ title, children, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <h3 className="font-semibold text-slate-900 mb-4">{title}</h3>
    {children}
  </div>
);

const Bars = ({ data, color }) => (
  <div className="h-56">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ left: -20, right: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
        <Tooltip />
        <Bar dataKey="count" fill={color} radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const Lines = ({ data, color }) => (
  <div className="h-56">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ left: -20, right: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="name" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
        <Tooltip />
        <Line type="monotone" dataKey="count" stroke={color} strokeWidth={2.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const Empty = () => <p className="text-sm text-slate-400 py-10 text-center">Pas encore de données.</p>;

export const AdminStats = ({ stats }) => (
  <div className="grid lg:grid-cols-2 gap-6">
    <Card title={`Agences par ville (${stats.agencies.total} · ${stats.agencies.pending} en attente)`} testid="admin-stats-agencies">
      {stats.agencies.by_city.length ? <Bars data={stats.agencies.by_city} color="#0B132B" /> : <Empty />}
    </Card>
    <Card title={`Véhicules par ville (${stats.vehicles.total})`} testid="admin-stats-vehicles">
      {stats.vehicles.by_city.length ? <Bars data={stats.vehicles.by_city} color="#006233" /> : <Empty />}
    </Card>
    <Card title={`Recherches par jour (${stats.searches.total} au total)`} testid="admin-stats-searches-day">
      {stats.searches.by_day.length ? <Lines data={stats.searches.by_day} color="#C1272D" /> : <Empty />}
    </Card>
    <Card title="Recherches par mois" testid="admin-stats-searches-month">
      {stats.searches.by_month.length ? <Bars data={stats.searches.by_month} color="#C1272D" /> : <Empty />}
    </Card>
    <Card title="Villes les plus actives" testid="admin-stats-top-cities">
      <ul className="space-y-2">
        {stats.top_cities.map((c, i) => (
          <li key={c.name} className="flex items-center gap-3 text-sm">
            <span className="w-6 text-slate-400 font-mono">{i + 1}.</span>
            <span className="flex-1 font-medium text-slate-800">{c.name}</span>
            <div className="h-2 rounded-full bg-slate-100 w-40 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(c.count / stats.top_cities[0].count) * 100}%`, background: "#D97706" }} /></div>
            <span className="w-8 text-right text-slate-500">{c.count}</span>
          </li>
        ))}
        {!stats.top_cities.length && <Empty />}
      </ul>
    </Card>
    <Card title={`Inscriptions par mois (${stats.registrations.total})`} testid="admin-stats-registrations">
      {stats.registrations.by_month.length ? <Bars data={stats.registrations.by_month} color="#0B132B" /> : <Empty />}
    </Card>
    <Card title="Demandes de location par statut" testid="admin-stats-requests">
      <div className="flex flex-wrap gap-3">
        {stats.requests.by_status.map((s) => (
          <div key={s.name} className="rounded-xl bg-slate-50 px-4 py-3"><p className="font-display text-2xl font-extrabold text-slate-900">{s.count}</p><p className="text-xs uppercase tracking-wide text-slate-500">{s.name}</p></div>
        ))}
        {!stats.requests.by_status.length && <Empty />}
      </div>
    </Card>
    <Card title="Historique des inscriptions" testid="admin-stats-history">
      <ul className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
        {stats.registrations.history.map((a) => (
          <li key={a.id} className="flex items-center justify-between py-2 text-sm">
            <div><p className="font-medium text-slate-800">{a.agency_name}</p><p className="text-xs text-slate-500">{a.city} · {a.email}</p></div>
            <span className="text-xs text-slate-400">{(a.created_at || "").slice(0, 10)}</span>
          </li>
        ))}
      </ul>
    </Card>
  </div>
);
